import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  RESPONSE_INIT,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CountryToken, EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { SEOService } from '@services/logic/seo.service';
import { TaskService } from '@services/http/task.service';
import { UserService } from '@services/http/user.service';
import { AssociatedUser } from '@components/seoPageElements/best-associated-users/associatedUser.type.interface';
import {
  MiniVertical,
  MiniVerticalService,
} from '@app/services/http/mini-verticals.service';
import { Task } from '@models/task.model';
import { toAbsoluteUrl, urlDecode } from '@utils/urlHelpers';
import { stripTrailingSiteName } from '@helpers/stripTrailingSiteName';
import { getSeoCountryConfig } from '@app/pages/seo/country-config';
import { computeLiveDisplayPrice } from '@utils/liveDisplayPrice';
import type { DisplayPrice } from '@models/vertical/displayPrice.model';
import {
  composeTitleWithPrice,
  containsPriceFigure,
  formatDescriptionPriceSentence,
  formatTitlePriceToken,
} from '@utils/displayPriceToken';
import { buildMiniVerticalBreadcrumbParts } from './mini-vertical-breadcrumb';
import { buildMiniVerticalProductSchema } from './mini-vertical-product-schema';
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { VerticalService } from '@services/http/verticals.service';
import { Vertical } from '@models/vertical.model';

// Components
import { AssociatedTasksComponent } from '@components/seo/associated-tasks/associated-tasks.component';
import { BestAssociatedUsersComponent } from '@components/seoPageElements/best-associated-users/best-associated-users.component';
import { HeroSectionComponent } from '@components/seo/hero-section/hero-section.component';
import { HowItWorksComponent } from '@components/seoPageElements/howItWorks/howItWorks.component';
import { GoodToKnowComponent } from '@components/seoPageElements/good-to-know/good-to-know.component';
import { LinksContainerComponent } from '@components/links-container/links-container.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { MiniVerticalPriceReviewComponent } from '@components/seoPageElements/mini-vertical-price-review/mini-vertical-price-review.component';
import { IncludeContentComponent } from '@components/seo/include-content/include-content.component';
import { FaqContentComponent } from '@components/seoPageElements/faq-content/faq-content.component';
import { RelatedServicesComponent } from '@components/seoPageElements/related-services/related-services.component';
import { ValueLineComponent } from '@components/seoPageElements/valueLine/valueLine.component';
import { WhyHandyhandComponent } from '@components/seoPageElements/whyPinployComponent/whyHandyhandComponent';
import { WhyContentComponent } from '@components/seoPageElements/why-content/why-content.component';
import { ExternalService } from '@app/services/http/external.service';
import { NavigationHistoryService } from '@app/services/logic/navigation-history.service';
import { merge, Subject } from 'rxjs';
import { TrustpilotWidgetComponent } from '@components/seoPageElements/trustpilot-widget/trustpilot-widget.component';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { MiniVerticalStatsComponent } from '@components/seoPageElements/mini-vertical-stats/mini-vertical-stats.component';
import { ImageViewerComponent } from '@components/shared/image-viewer/image-viewer.component';
import { SimilarTasksComponent } from '@components/seoPageElements/similar-tasks/similar-tasks.component';
import { LocalizationService } from '@services/logic/localization.service';

/** Meta descriptions past this are truncated in the SERP. */
const META_DESC_MAX_LENGTH = 160;

@Component({
  imports: [
    LinksContainerComponent,
    FooterComponent,
    JsonLdPipe,
    HeroSectionComponent,
    ValueLineComponent,
    BestAssociatedUsersComponent,
    HowItWorksComponent,
    MiniVerticalPriceReviewComponent,
    AssociatedTasksComponent,
    IncludeContentComponent,
    WhyHandyhandComponent,
    FaqContentComponent,
    GoodToKnowComponent,
    WhyContentComponent,
    RelatedServicesComponent,
    TrustpilotWidgetComponent,
    BreadcrumbListComponent,
    MiniVerticalStatsComponent,
    ImageViewerComponent,
    SimilarTasksComponent,
  ],
  providers: [
    DecimalPipe,
    SEOService,
    TranslatePipe,
    ExternalService,
    NavigationHistoryService,
  ],
  selector: 'app-mini-vertical',
  templateUrl: './mini-vertical.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mini-vertical.component.scss'],
})
export class MiniVerticalComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly country = inject(CountryToken);
  private readonly localization = inject(LocalizationService);
  private readonly seoCountryConfig = getSeoCountryConfig(this.country);

  public isBrowser: boolean;
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  @ViewChild(TrustpilotWidgetComponent, { static: false })
  trustpilotWidget?: TrustpilotWidgetComponent;
  private destroy$ = new Subject<void>();
  private cancel$ = new Subject<void>();
  relTasks: Task[] = [];
  miniVertical!: MiniVertical;
  relatedVertical?: Vertical;
  canonicalUrl?: string;
  category: any;
  partsOfUrl: { name: string; url: string }[] = [];
  users: AssociatedUser[] = [];
  similarTasksForStats: any[] = [];

  private verticalSlug!: string;

  priceReviewShowing?: boolean;

  getQuoteChosen?: string;
  content: any;
  q25Price: number = 0;
  q50Price: number = 0;
  q75Price: number = 0;
  /**
   * The one price this page publishes, or null. Derived from the document's own
   * offer bids — see {@link trustedLivePrice}. Held as state rather than
   * recomputed per surface so the `<title>`, the meta description and the
   * visible band cannot quote different numbers.
   */
  private livePrice: DisplayPrice | null = null;

  schema: any;
  url?: string;

  isFindPage = true; // Mini-verticals are always find pages
  isJobsPage = false; // Mini-verticals don't have job pages
  noRelTasks = false;
  priceAndReviewRendered = false;
  hasAssociatedTasks = false;
  hasFaqContent = false;
  hasGoodToKnow = false;
  verticalH1?: string;
  hasWhatItIncludesArea = false;
  goodToKnowContent?: string;
  relatedServicesContent?: any;
  hasVertical = true;
  public heroImgUrl: string = '';
  public innerWidth: number = 1200;
  private isLoadingData = false;
  private hasProcessedData = false;
  private hydrationTimeout?: number;

  constructor(
    private activatedRoute: ActivatedRoute,
    public externalService: ExternalService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private seoService: SEOService,
    private taskService: TaskService,
    private userService: UserService,
    private miniVerticalService: MiniVerticalService,
    private verticalService: VerticalService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private translate: TranslatePipe,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    afterNextRender(() => {
      this.innerWidth = window.innerWidth;
    });
  }

  ngOnInit(): void {
    this.url = this.router.url.split('/')[1];

    // Check for duplicate parameters and redirect if found
    this.checkAndRedirectForDuplicates();

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // Cancel any in-flight requests from previous route
        this.cancel$.next();

        this.isLoadingData = false;
        this.hasProcessedData = false;

        this.verticalSlug = params.get('verticalSlug') ?? '';

        this.fetchMiniVertical(urlDecode(this.verticalSlug).toLowerCase());
      });
  }

  /**
   * Check for duplicate parameters in URL and redirect if found
   */
  private checkAndRedirectForDuplicates(): void {
    if (!this.isBrowser) return;
    const cleanedUrl = this.removeDuplicateParams(this.router.url);

    // If the cleaned URL is different from current URL, redirect
    if (cleanedUrl !== this.router.url) {
      const fullCleanedUrl = `${this.env.domain}${cleanedUrl}`;
      // Use 303 redirect (See Other) for SEO-friendly redirect
      window.location.replace(fullCleanedUrl);
    }
  }

  /**
   * Removes duplicate consecutive parameters from URL path
   * Example: /rengoering/rengoering/hh becomes /rengoering/hh
   * @param urlPath - The URL path to clean
   * @returns Cleaned URL path with duplicates removed
   */
  private removeDuplicateParams(urlPath: string): string {
    if (!urlPath) return urlPath;

    const segments = urlPath.split('/').filter((segment) => segment.length > 0);
    const cleanedSegments: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      const currentSegment = segments[i];
      const nextSegment = segments[i + 1];

      // Only add current segment if it's not the same as the next one
      if (currentSegment !== nextSegment) {
        cleanedSegments.push(currentSegment);
      }
    }

    return '/' + cleanedSegments.join('/');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancel$.next();
    this.cancel$.complete();

    if (this.hydrationTimeout) {
      clearTimeout(this.hydrationTimeout);
    }
  }

  private navigateTo(url: string) {
    // skipLocationChange: render the 404 on the requested URL — an in-render
    // navigation during SSR would otherwise become an invalid 404 "redirect".
    return this.router.navigate([`/${url}`], { skipLocationChange: true });
  }

  private processMiniVerticalData(miniVertical: MiniVertical): void {
    if (this.hasProcessedData) {
      return;
    }

    this.hasProcessedData = true;
    this.miniVertical = miniVertical;
    this.hasVertical = true;
    this.priceAndReviewRendered = true;

    // Set hero image
    if (miniVertical.heroImg) {
      this.heroImgUrl = miniVertical.heroImg;
    }

    // The visible band. Whatever the `<title>` quotes has to come from the same
    // place, or the SERP promises a price the page then contradicts — the exact
    // failure DISPLAY-PRICE-SPEC.md exists to prevent. So the trusted live band
    // wins here too, and the curated guide stays as the fallback for the pages
    // that have no bids to derive one from.
    //
    // Without this the two disagree on 253 of the 513 pages that gain a price
    // in the title, always in the direction of the title undercutting the page.
    this.livePrice = this.trustedLivePrice();
    if (this.livePrice) {
      this.q25Price = this.livePrice.min;
      this.q50Price = this.livePrice.middle ?? this.livePrice.min;
      this.q75Price = this.livePrice.max;
    } else if (miniVertical.price_guide) {
      this.q25Price = miniVertical.price_guide.min || 0;
      this.q50Price = miniVertical.price_guide.middle || 0;
      this.q75Price = miniVertical.price_guide.max || 0;
    }

    // Handle associated tasks
    // Prefer taskExamples for display (has full task details like description and budget)
    if (miniVertical.taskExamples && miniVertical.taskExamples.length > 2) {
      this.relTasks = miniVertical.taskExamples as Task[];
      this.hasAssociatedTasks = this.relTasks.length > 0;
      this.setAssociatedTasks(this.relTasks);

      // If similarTasks also exists, use it to extract user IDs from offers
      if (miniVertical.similarTasks && miniVertical.similarTasks.length > 0) {
        this.similarTasksForStats = miniVertical.similarTasks;
        this.fetchUsersFromSimilarTasks(miniVertical.similarTasks);
      }
    } else if (
      miniVertical.similarTasks &&
      miniVertical.similarTasks?.length > 2
    ) {
      this.relTasks = miniVertical.similarTasks as Task[];
      this.hasAssociatedTasks = this.relTasks.length > 0;
      this.setAssociatedTasks(this.relTasks);

      // Extract and fetch users from similarTasks
      this.similarTasksForStats = miniVertical.similarTasks;
      this.fetchUsersFromSimilarTasks(miniVertical.similarTasks);
    } else if (miniVertical.tagId) {
      this.fetchAssociatedTasks(miniVertical.tagId);
    }
    this.getQuoteChosen = miniVertical.s1;
    this.hasFaqContent = !!miniVertical.mFAQ;
    this.hasGoodToKnow = !!miniVertical.content?.GOOD_TO_KNOW_area;
    this.verticalH1 = miniVertical.h1;
    this.hasWhatItIncludesArea = !!miniVertical.mProvide;
    this.goodToKnowContent = miniVertical.content?.GOOD_TO_KNOW_area;
    this.relatedServicesContent = miniVertical.content;

    this.injectJSONLDSchema();
    this.setMetaData();
    this.setBreadCrumb();
    this.reinitializeTrustpilotWidget();

    // Fetch the related vertical if available
    if (miniVertical.relatedVertical?.slug) {
      this.fetchRelatedVertical(miniVertical.relatedVertical.slug);
    }

    this.cdr.markForCheck();
  }

  private fetchMiniVertical(slug: string) {
    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;

    // No manual TransferState here: the built-in HTTP transfer cache
    // (withHttpTransferCacheOptions in app.config.ts) already carries this
    // response to the client — a manual copy duplicated the full unprojected
    // mini-vertical document (~47 bids) in every page's ng-state.
    this.miniVerticalService
      .fetchMiniVerticalById(slug)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (miniVertical: MiniVertical): void => {
          this.processMiniVerticalData(miniVertical);
          this.isLoadingData = false;
        },
        (err) => {
          this.isLoadingData = false;

          if (err.status === 404) {
            // Cancel all in-flight requests (related vertical, users, etc.)
            this.cancel$.next();
            if (this.responseInit) {
              this.responseInit.status = 404;
            }
            return this.navigateTo('404');
          }

          this.ngZone.run(() => {
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          });

          return err;
        },
      );
  }

  private fetchRelatedVertical(slug: string): void {
    this.verticalService
      .fetchVerticalById(
        slug,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )
      .pipe(takeUntil(merge(this.destroy$, this.cancel$)))
      .subscribe({
        next: (vertical: Vertical) => {
          this.relatedVertical = vertical;
          this.cdr.markForCheck();
        },
        error: () => {
          this.relatedVertical = undefined;
          this.cdr.markForCheck();
        },
      });
  }

  private reinitializeTrustpilotWidget(): void {
    if (this.isBrowser) {
      requestAnimationFrame(() => {
        if (this.trustpilotWidget) {
          this.trustpilotWidget.reinitialize();
        }
      });
    }
  }

  private setAssociatedTasks(tasks: Task[]): void {
    this.relTasks = tasks;
    this.hasAssociatedTasks = tasks.length > 0;
    this.noRelTasks = !this.hasAssociatedTasks;
  }

  private fetchAssociatedTasks(tagId: string): void {
    const tagIdNumber = parseInt(tagId, 10);
    if (isNaN(tagIdNumber)) {
      this.noRelTasks = true;
      return;
    }

    this.taskService
      .fetchAssociatedTasksByTag(tagIdNumber, undefined)
      .pipe(takeUntil(merge(this.destroy$, this.cancel$)))
      .subscribe({
        next: (tasks: Task[]) => {
          this.setAssociatedTasks(tasks);
          this.cdr.markForCheck();
        },
        error: () => {
          this.noRelTasks = true;
          this.cdr.markForCheck();
        },
      });
  }

  private fetchUsersFromSimilarTasks(similarTasks: any[]): void {
    // Extract unique user IDs from similarTasks.offers[].user.id
    const userIds = new Set<number>();

    similarTasks.forEach((task) => {
      if (task.offers && Array.isArray(task.offers)) {
        task.offers.forEach((offer: any) => {
          // Check if user is an object with id property
          if (offer.user && typeof offer.user === 'object' && offer.user.id) {
            userIds.add(offer.user.id);
          }
          // Check if user is directly a number (fallback)
          else if (offer.user && typeof offer.user === 'number') {
            userIds.add(offer.user);
          }
        });
      }
    });

    const userIdsArray = Array.from(userIds);

    if (userIdsArray.length === 0) {
      return;
    }

    this.userService
      .fetchUsersByIds(userIdsArray)
      .pipe(takeUntil(merge(this.destroy$, this.cancel$)))
      .subscribe({
        next: (users: AssociatedUser[]) => {
          this.users = this.selectTopUsers(users, 6);
          this.cdr.markForCheck();
        },
        error: () => {
          this.users = [];
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * The one absolute, slash-normalized URL this page publishes — the Product's
   * `url`/`@id` and the canonical both read it, so they cannot drift apart the
   * way a raw routed path (double slashes survive `toAbsoluteUrl`) or a
   * relative stored `url` would let them.
   *
   * 1,207 of the 4,429 indexable documents carry no `url` field; those fall
   * back to the routed URL. The previous `env.appUrl` fallback pointed the
   * page's primary entity at the platform app instead.
   */
  private resolvePageUrl(): string {
    const routedPath = this.router.url.split(/[?;#]/)[0] || '/';
    const absolute = toAbsoluteUrl(
      this.env.domain,
      this.miniVertical?.url || routedPath,
    );
    // Same normalization SEOService.setUrl applies to the canonical tag.
    const schemeEnd = absolute.indexOf('://') + 3;
    return (
      absolute.slice(0, schemeEnd) +
      absolute.slice(schemeEnd).replace(/\/{2,}/g, '/')
    );
  }

  private injectJSONLDSchema(): void {
    if (!this.miniVertical) return;

    const pageUrl = this.resolvePageUrl();

    // Product + AggregateOffer, same entity pattern as `/hh`. The band is the
    // resolved q25/q75 state — the ONE price this page publishes everywhere
    // (title, description, widget), so the markup cannot quote a number the
    // page contradicts. No aggregateRating: the LocalBusiness entity this
    // replaces carried one fabricated from bidders' profile stars × 29 — see
    // mini-vertical-product-schema.ts for the full rationale.
    // `task` is typed string (legacy) but arrives as an object on all 4,429
    // current documents — falling back to it unchecked would embed the whole
    // task record as the Product's name.
    const legacyTaskName =
      typeof this.miniVertical.task === 'string' ? this.miniVertical.task : '';
    const { schema, warnings } = buildMiniVerticalProductSchema({
      name: this.miniVertical.h1 || legacyTaskName,
      description: this.miniVertical.mDescription || this.miniVertical.tT || '',
      url: pageUrl,
      image: this.miniVertical.heroImg,
      brandName: this.env.appName,
      priceCurrency: this.localization.currencyCode().toUpperCase(),
      // Both bounds or nothing: a half-populated band is absent data, and the
      // builder's contract reserves warnings for implausible data.
      band:
        this.q25Price > 0 && this.q75Price > 0
          ? { low: this.q25Price, high: this.q75Price }
          : undefined,
      averageOfferCount: this.miniVertical.taskStatistics?.averageOfferCount,
    });

    // Loud on the server so a recurrence of the 100x unit bug surfaces in the
    // PM2 logs instead of shipping as markup. The page itself renders on.
    // Server-only: after client-side navigation this would land in end users'
    // consoles instead.
    if (!this.isBrowser) {
      for (const warning of warnings) {
        console.error(`[mini-vertical JSON-LD] ${pageUrl}: ${warning}`);
      }
    }

    this.schema = schema;
  }

  private injectBreadcrumbSchema(): void {
    if (this.partsOfUrl.length === 0) return;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        // Home
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': this.env.domain,
            name: this.translate.transform('SEO.SCHEMA.BREADCRUMB_HOME'),
          },
        },
        // Add all breadcrumb parts
        ...this.partsOfUrl.map((part, index) => ({
          '@type': 'ListItem',
          position: index + 2,
          item: {
            '@id': toAbsoluteUrl(this.env.domain, part.url),
            name: part.name,
          },
        })),
      ],
    };

    if (this.schema) {
      this.schema = {
        '@context': 'https://schema.org',
        '@graph': [this.schema, breadcrumbSchema],
      };
    } else {
      this.schema = breadcrumbSchema;
    }
  }

  private setMetaData(): void {
    if (!this.miniVertical) return;

    // Many of these titles have "| Handyhand" baked into the CMS copy, and
    // SeoService appends " - Handyhand" to every title. Strip the authored
    // suffix so the brand appears once instead of twice, which also reclaims
    // ~12 of the ~60 characters Google displays.
    const base = stripTrailingSiteName(
      this.miniVertical.mT ||
        this.miniVertical.tT ||
        this.miniVertical.h1 ||
        '',
    );

    // Resolved once in `processMiniVerticalData`, which is the only caller —
    // the title, the description and the visible band read that one value so
    // they cannot drift apart.
    const price = this.livePrice;

    // Same trade as `/hh`: the brand suffix yields to a price, never the
    // reverse. It is byte-identical on every mini-service and so differentiates
    // nothing, whereas the price is specific to this one service.
    // Withhold the token when the stored title already quotes a figure, rather
    // than guarding inside `composeTitleWithPrice`: the two templates handle an
    // authored price differently on purpose. `/pris-data` takes it OFF with
    // `stripAuthoredPriceTail` and appends a canonical one, and deliberately
    // leaves figures that sit mid-string because there they describe the job
    // («12 kvm badeværelse»). A guard in the shared function would suppress the
    // token on exactly those pages. Mini-services strip nothing, so here the
    // authored figure simply wins — prospectively: no document carries one today.
    const token = containsPriceFigure(base)
      ? null
      : formatTitlePriceToken(
          price ?? undefined,
          this.seoCountryConfig.priceToken,
        );
    const composed = composeTitleWithPrice(
      base,
      token,
      ` - ${this.env.appName}`,
    );

    // Same normalized absolute URL the Product schema publishes — one source,
    // so the canonical and the entity's url/@id are byte-identical.
    this.canonicalUrl = this.resolvePageUrl();
    this.seoService.setPageMeta({
      metaTitle: composed.title,
      metaDesc: this.composeMetaDescription(price),
      canonicalUrl: this.canonicalUrl,
      img: this.miniVertical.heroImg,
      appendSiteName: composed.appendSiteName,
    });

    // Pruned pages: keep serving the page (HTTP 200), drop it from the index.
    // `follow` so link equity still flows on to the hub and related services.
    // Mirrors the per-vertical / per-geo switch in seo.component.ts.
    this.seoService.setRobotsNoindex(!!this.miniVertical.noindex);
  }

  /**
   * The live price for this mini-service, but only when the bids agree closely
   * enough for its low end to mean anything.
   *
   * `/hh` verticals publish `high` OR `medium`. Mini-services must not, and the
   * reason is the size and provenance of the bid pool: a vertical aggregates
   * every task carrying its tag, while a mini-service aggregates ~13
   * vector-similarity matches. That pool carries a heavy cluster of placeholder
   * bids at exactly the platform minimum — 10% of all bids on average, and past
   * a quarter of them on 142 pages — and once the cluster crosses a quarter,
   * q25 *is* the floor.
   *
   * Measured on `montering-velux-ovenlysvinduer-eternittag`: 22 bids, six of
   * them at 205 kr, real quotes at 4.800-34.000, curated guide 9.000-15.000 —
   * and a q25 of 220 kr. Publishing "fra 220 kr." for nine Velux windows is a
   * worse lie than the inflated guide it would replace.
   *
   * `high` requires CV < 0.6, which is precisely a bid pool without that
   * skew. Across the corpus it takes the count of from-prices under a fifth of
   * their own average from 296 to zero, at the cost of 1,860 priced titles down
   * to 513. Fewer, and none of them absurd.
   */
  private trustedLivePrice(): DisplayPrice | null {
    const price = computeLiveDisplayPrice(this.miniVertical);
    return price?.confidence === 'high' ? price : null;
  }

  /**
   * The meta description, price sentence included when one can be derived.
   *
   * `mDescription` doubles as the visible hero subtitle and city URLs
   * concatenate a place name after it, so the price is composed here for the
   * meta tag only and never written back to the page copy.
   *
   * Precedence is deliberate. `mDescriptionMeta` is a stored variant whose
   * price is `price_guide.min` verbatim (2,850 of 2,850 shipped values), and
   * that guide is shared by up to 1,091 mini-services — it sits ABOVE the
   * average of real bids on 22.5% of pages, i.e. it advertises a floor higher
   * than what the work typically goes for. So a live-derived sentence wins
   * wherever we have bids, and the stored variant is kept only as the fallback
   * for pages that have none, where it is still better than no price at all.
   *
   * The two sources must never BOTH appear on one page. They disagree — the
   * `<title>` would read "fra 750 kr." beside a description reading "Fra 500
   * kr." (observed, before this branch existed).
   *
   * The gate is the PRICE, not the title. An earlier revision gated on whether
   * the title had found room for the figure, reasoning that a page quoting
   * nothing yet could still use the stored variant. That was wrong: the visible
   * band renders the live figures whenever a trusted price exists, so the stored
   * variant would contradict the widget even when the title stayed silent. It
   * bought 100 pages a description price when `mDescriptionShort` did not exist;
   * now that it does, the sentence fits somewhere on all but 5 trusted-price
   * pages, and exactly ONE of those five would keep a price under the looser
   * gate. One page is not worth a surface that disagrees with itself.
   */
  private composeMetaDescription(price: DisplayPrice | null): string {
    const body = this.miniVertical?.mDescription ?? '';

    // Authored copy that already quotes a figure keeps it, and gets nothing
    // appended — same rule as the title. `mDescriptionMeta` is not a fallback
    // here either: it would append a second, guide-derived figure.
    if (containsPriceFigure(body)) {
      return body;
    }

    const sentence = formatDescriptionPriceSentence(
      price ?? undefined,
      this.seoCountryConfig.priceToken,
    );
    if (sentence) {
      // Full copy first — it says more, and `mDescriptionShort` only exists on
      // the pages where it does not fit. Trying it in this order means the
      // shortened rewrite is used exactly where it earns its place.
      const bases = [body, this.miniVertical?.mDescriptionShort ?? ''];
      for (const base of bases) {
        if (!base || containsPriceFigure(base)) continue;
        const composed = `${base} ${sentence}`;
        if (composed.length <= META_DESC_MAX_LENGTH) {
          return composed;
        }
      }
    }

    // Past the cap Google truncates mid-sentence and the price is what gets cut.
    // Once a trusted price exists the widget is already quoting it, so the
    // guide-derived stored variant may not stand in — the description goes
    // without rather than contradict the band beside it.
    return price ? body : this.miniVertical?.mDescriptionMeta || body;
  }

  private setBreadCrumb(): void {
    this.partsOfUrl = buildMiniVerticalBreadcrumbParts(
      this.miniVertical,
      this.seg.suffix.hubIndex,
    );

    this.injectBreadcrumbSchema();
  }

  setGetQuote(value: string): void {
    this.getQuoteChosen = value;
  }

  openAuthDialog(): void {}

  get vertical(): any {
    return this.miniVertical;
  }

  getPriceGuideLink(): string | null {
    // Prefer AI price guide if available
    if (this.miniVertical?.aiPriceGuideUrl) {
      return this.miniVertical.aiPriceGuideUrl;
    }

    return null;
  }

  private selectTopUsers(
    users: AssociatedUser[],
    limit: number,
  ): AssociatedUser[] {
    if (!users || users.length === 0) {
      return [];
    }

    const sortedUsers = users.sort((a, b) => {
      // Priority 1: Users with mitId verification
      const aIsIdVerified = a?.identificationVerified;
      const bIdVerified = b?.identificationVerified;

      if (aIsIdVerified && !bIdVerified) return -1;
      if (!aIsIdVerified && bIdVerified) return 1;

      // Priority 2: Users with profile images
      const aHasImage = a.profileImg && a.profileImg.trim() !== '';
      const bHasImage = b.profileImg && b.profileImg.trim() !== '';

      if (aHasImage && !bHasImage) return -1;
      if (!aHasImage && bHasImage) return 1;

      // Priority 3: Users with more reviews
      const aReviewCount = a.receivedReviews ? a.receivedReviews.length : 0;
      const bReviewCount = b.receivedReviews ? b.receivedReviews.length : 0;

      if (aReviewCount !== bReviewCount) {
        return bReviewCount - aReviewCount; // More reviews first
      }

      // Priority 4: Users with higher average rating
      const aAvgRating = a.rating || 0;
      const bAvgRating = b.rating || 0;

      return bAvgRating - aAvgRating; // Higher rating first
    });
    return sortedUsers.slice(0, limit);
  }

  selectedTaskImages: { url: string; alt?: string }[] = [];
  selectedImageIndex = 0;
  showImageViewer = false;

  get processedSimilarTasks() {
    if (!this.similarTasksForStats || this.similarTasksForStats.length === 0) {
      return [];
    }

    return this.similarTasksForStats
      .map((task) => ({
        ...task,
        averageOfferPrice: this.calculateAverageOfferPrice(task.offers || []),
      }))
      .sort((a, b) => {
        const aHasImages = a.images && a.images.length > 0;
        const bHasImages = b.images && b.images.length > 0;

        if (aHasImages && !bHasImages) return -1;
        if (!aHasImages && bHasImages) return 1;
        return 0;
      });
  }

  private calculateAverageOfferPrice(
    offers: Array<{ priceBid: number }>,
  ): number {
    if (!offers.length) return 0;
    const sum = offers.reduce((acc, offer) => acc + offer.priceBid, 0);
    return Math.round(sum / offers.length);
  }

  onImageViewerOpen(event: { images: string[]; index: number }) {
    this.selectedTaskImages = event.images.map((url) => ({
      url,
      alt: this.miniVertical?.mT || '',
    }));
    this.selectedImageIndex = event.index;
    this.showImageViewer = true;
    this.cdr.markForCheck();
  }

  closeImageViewer() {
    this.showImageViewer = false;
    this.cdr.markForCheck();
  }
}
